import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import Api, { executeAction, loadPauseReasons } from '../../../helpers/api';
import { tableConfig } from '../../../settings/index';
import appActions from '../../../redux/app/actions';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import notifications from '../../../redux/notifications/actions';
import { keyByProperty } from '../../../helpers/core';
import { t } from '../../../i18n';

const api = new Api('jobs/:id');
const apiPauseMa = new Api('matching_engine/:id/pause');
const apiResumeMa = new Api('matching_engine/:id/resume');
const rostersApi = new Api('jobs/:id/rosters/export');
const loadShiftApi = new Api('bookings/:id/shifts');
const loadCancelledShiftApi = new Api('bookings/:id/cancelled_shifts');
const loadBackupShiftApi = new Api('bookings/:id/backup_shifts');
const loadReportedShiftApi = new Api('bookings/:id/reported_shifts');
const reopenPoolsApi = new Api('matching_engine/:id/open_pools');
const wokerSubmissionsApi = new Api('bookings/:id/worker_submissions');

function getIndexedArray(array, startIndex) {
  return array.map((item, index) => ({ ...item, index: startIndex + index }));
}

function getEmptyPaginatedResult(bookingId) {
  return {
    data: { page: 1, data: [], totalCount: 0, filteredCount: 0, sortable_columns: [], bookingId },
  };
}

function* isWorkerSubmissionsEnabled() {
  const state = yield select();

  return (
    state.StartAppSettings?.feature_flags?.flagged_timesheets_enabled &&
    state.StartAppSettings?.organization_settings?.allow_admins_hours_review
  );
}

function* loadPaginatedData(apiMethod, payload, successAction) {
  try {
    const { page = 1, pageSize = tableConfig.pageSize, column, order, filter = '', id, ...rest } = payload;
    const result = yield call(
      sendRequest,
      apiMethod,
      { search: { page, pageSize, column, order, filter, ...rest }, id },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      const { records, totalCount, filteredCount, sortable_columns } = result.data.success.data;
      const indexedData = getIndexedArray(records, (page - 1) * pageSize);
      const data = { page, data: indexedData, totalCount, filteredCount, sortable_columns, bookingId: id };

      if (successAction) {
        yield put(successAction(data));
      }

      return { data };
    }

    return result;
  } catch (error) {
    logger.error(error);
  }
}

function* reopenPools({ payload }) {
  const { id } = payload;

  try {
    const result = yield call(sendRequest, reopenPoolsApi.createItem, { id }, notifications.REQUEST_ERROR);

    if (result?.status === 200) {
      yield put(actions.reopenPoolsSuccess());
      yield put(actions.loadData({ id, query: {} }));
    } else {
      yield put(actions.reopenPoolsError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadShifts({ payload }) {
  if (payload.added_by_worker_submission) {
    const enabled = yield call(isWorkerSubmissionsEnabled);

    if (!enabled) return getEmptyPaginatedResult(payload.id);

    return yield call(loadPaginatedData, loadShiftApi.load, payload, null);
  }

  return yield call(loadPaginatedData, loadShiftApi.load, payload, actions.loadShiftsSuccess);
}

function* loadWorkerSubmissions({ payload }) {
  const enabled = yield call(isWorkerSubmissionsEnabled);

  if (!enabled) return getEmptyPaginatedResult(payload.id);

  return yield call(loadPaginatedData, wokerSubmissionsApi.load, payload, actions.loadWorkerSubmissionsSuccess);
}

function* loadCancelledShifts({ payload }) {
  return yield call(loadPaginatedData, loadCancelledShiftApi.load, payload, actions.loadShiftsSuccess);
}

function* loadBackupShifts({ payload }) {
  const { page, pageSize, column, order, filter, id } = payload;

  return yield call(
    loadPaginatedData,
    loadBackupShiftApi.load,
    { page, pageSize, column, order, filter, id },
    actions.loadBackupShiftsSuccess,
  );
}

function* loadReportedShifts({ payload }) {
  return yield call(loadPaginatedData, loadReportedShiftApi.load, payload, actions.loadReportedShiftsSuccess);
}

function* loadApprovedAfterJobClosedRequest({ payload }) {
  const enabled = yield call(isWorkerSubmissionsEnabled);

  if (!enabled) return getEmptyPaginatedResult(payload.id);

  return yield call(
    loadPaginatedData,
    loadShiftApi.load,
    { ...payload, added_by_worker_submission: true },
    actions.loadApprovedAfterJobClosedSuccess,
  );
}

// Each booking's lists are independent, so they load together rather than one booking at a time,
// which made load time grow with the booking count (DFW-4115). Bookings go in batches so a job with
// many of them does not open dozens of connections at once.
export const BOOKING_BATCH_SIZE = 6;

export function* loadBookingLists(booking, query, pages) {
  let shifts;
  let backupShifts;
  let reportedShifts;
  let approvedAfterJobClosed;
  let addedAfterJobClosed;

  if (!booking.removed) {
    ({ shifts, backupShifts, reportedShifts, approvedAfterJobClosed, addedAfterJobClosed } = yield all({
      shifts: call(loadShifts, {
        payload: {
          ...(pages.query || {}),
          id: booking.id,
          ...(query && query.tab === 'bookings' ? { ...query } : {}),
          page: pages.currentShiftsPage,
        },
      }),
      approvedAfterJobClosed: call(loadShifts, {
        payload: {
          id: booking.id,
          added_by_worker_submission: true,
        },
      }),
      addedAfterJobClosed: call(loadWorkerSubmissions, {
        payload: {
          id: booking.id,
        },
      }),
      backupShifts: call(loadBackupShifts, {
        payload: { ...booking, page: pages.currentBackupsShiftsPage },
      }),
      reportedShifts: call(loadReportedShifts, {
        payload: { id: booking.id, page: pages.currentReportedShiftsPage },
      }),
    }));
  }

  // Runs after the others, as it did when bookings loaded serially: this and `loadShifts` both
  // dispatch LOAD_SHIFTS_SUCCESS for the booking, and the reducer keeps whichever lands last.
  const cancelledShifts = yield call(loadCancelledShifts, {
    payload: { id: booking.id, page: pages.currentReportedShiftsPage },
  });

  booking.shifts = shifts?.data || [];
  booking.shifts_page = shifts?.page || 1;

  booking.backup_shifts = backupShifts?.data || [];
  booking.backup_shifts_page = backupShifts?.page || 1;

  booking.reported_shifts = reportedShifts?.data || [];
  booking.reported_shifts_page = reportedShifts?.page || 1;

  booking.cancelled_shifts = cancelledShifts?.data || [];
  booking.cancelled_shifts_page = cancelledShifts?.page || 1;

  booking.approved_after_job_closed_shifts = approvedAfterJobClosed?.data || [];
  booking.approved_after_job_closed_page = approvedAfterJobClosed?.page || 1;

  booking.added_after_job_closed_shifts = addedAfterJobClosed?.data || [];
  booking.added_after_job_closed_page = addedAfterJobClosed?.page || 1;
}

export function* loadData({ payload }) {
  try {
    const { id, query, pages = {} } = payload;

    yield put(appActions.changeBreadcrumbState({ prop: 'jobs', value: '...' }));
    yield put(appActions.changeBreadcrumbState({ prop: 'jobs/:id', value: '' }));

    const { result, formData } = yield all({
      result: call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR),
      formData: call(sendRequest, api.loadFormData, {}, notifications.REQUEST_ERROR),
    });

    if (result?.data?.success?.data && formData) {
      const { data } = result.data.success;
      const breadcrumbJobsState = yield select(state => state.App.breadcrumbState);

      if (data.bookings && data.bookings.length !== 0) {
        const { bookings } = data;

        for (let i = 0; i < bookings.length; i += BOOKING_BATCH_SIZE) {
          yield all(
            bookings.slice(i, i + BOOKING_BATCH_SIZE).map(booking => call(loadBookingLists, booking, query, pages)),
          );
        }
      }

      // Silently update url so we can show 'Multi-day Job' breadcrumb
      if (data.job_group_id) {
        window.history.replaceState(null, null, `/jobs/multiDay/${id}?tab=bookings`);
      }

      yield put(
        actions.loadDataSuccess({
          data: {
            job: data,
            ...formData.data,
          },
        }),
      );

      if (!breadcrumbJobsState?.jobs?.label) {
        yield put(
          appActions.changeBreadcrumbState({
            prop: 'jobs',
            value: {
              label: `${t('dash.jobs.jobs_title')} -
                ${data.time_type.charAt(0).toUpperCase()}${data.time_type.slice(1)}`,
              path: `/jobs?tab=${data.time_type}`,
            },
          }),
        );
      }

      if (!data.job_group_id) {
        yield put(
          appActions.changeBreadcrumbState({
            prop: 'jobs/:id',
            value: data.client.name,
          }),
        );
      }

      if (data.job_group_id) {
        yield put(
          appActions.changeBreadcrumbState({
            prop: 'jobs/multiDay',
            value: {
              label: t('dash.jobs.multi_day_job_label'),
              path: `/jobs/multiDay?tab=${data.time_type}&job_group_id=${data.job_group_id}`,
            },
          }),
        );

        yield put(
          appActions.changeBreadcrumbState({
            prop: 'jobs/multiDay/:id',
            value: data.client.name,
          }),
        );
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteJob(data) {
  try {
    const { payload } = data;
    const { method, url } = payload;

    const result = yield call(sendRequest, executeAction, { url, method }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(actions.deleteSuccess());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.deleted_successfully', { item: t('dash.jobs.job_title') }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateJob({ payload }) {
  try {
    const { editForm, id, notify_workers } = payload;
    const search = notify_workers ? { notify_workers } : null;

    const propsArray = Object.values(editForm);
    const job = keyByProperty(propsArray, 'name', prop => prop.value);

    if (job.location) {
      job.latitude = job.location.lat;
      job.longitude = job.location.lng;
      delete job.location;
    }
    job.country_id = job.country;

    const result = yield call(sendRequest, api.updateItem, { data: { job }, id, search }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(actions.saveSuccess({ data: job }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.updated_successfully', { item: t('dash.jobs.job_title') }),
        }),
      );
      yield loadData({ payload: { id } });
    }
  } catch (error) {
    logger.error(error);
  }
}

function* sendActionRequest({ payload }) {
  try {
    const { url, method, interval } = payload;

    const result = yield call(
      sendRequest,
      executeAction,
      { url, method, data: { interval } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.messages) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: result.messages.join(' '),
        }),
      );
      yield put(actions.closeModal());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* approveJobRequest({ payload }) {
  try {
    const { url, method, interval, id, query } = payload;

    const result = yield call(
      sendRequest,
      executeAction,
      { url, method, data: { interval } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.jobs.job_approved_message'),
        }),
      );

      yield put(actions.loadData({ id, query }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* getRosters(data) {
  try {
    const { payload } = data;
    const { fileType, id, fileName } = payload;

    const result = yield call(
      sendRequest,
      rostersApi.downloadFile,
      {
        id,
        fileType,
      },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      const link = document.createElement('a');
      link.href = result.data.fileBlobUrl;
      link.target = '_blank';
      link.download = result.data.fileName || fileName || `roster-${Math.random().toString(36).slice(-5)}`;

      if (!link.download.endsWith(`.${fileType}`)) {
        link.download += `.${fileType}`;
      }

      link.click();
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.exported_successfully', { item: t('dash.jobs.rosters_title') }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* getPauseReasonsRequest(payload) {
  const { status } = payload.payload;
  try {
    const result = yield call(loadPauseReasons, status);

    if (result && result.data) {
      const { data } = result;
      yield put(actions.getPauseReasons(data));
    }
  } catch (error) {
    yield put(notifications.REQUEST_ERROR);
    logger.error(error);
  }
}

function* onPauseMaRequest(data) {
  try {
    const { reason_id, id, description } = data.payload;
    const result = yield call(
      sendRequest,
      apiPauseMa.createItem,
      { id, data: { reason_id, description } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.closeModal());
      yield put(actions.updatePauseMaAction());
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.jobs.notifications.ma_paused_notification_success_message'),
        }),
      );
    }
  } catch (error) {
    yield put(notifications.REQUEST_ERROR);
    logger.error(error);
  }
}

function* onResumeMaRequest(data) {
  try {
    const { reason_id, id, description } = data.payload;
    const result = yield call(
      sendRequest,
      apiResumeMa.createItem,
      { id, data: { reason_id, description } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(actions.closeModal());
      yield put(actions.updateResumeMaAction());

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.jobs.notifications.ma_resumed_notification_success_message'),
        }),
      );
    }
  } catch (error) {
    yield put(notifications.REQUEST_ERROR);
    logger.error(error);
  }
}

function* showConsolidatedInvoice({ payload }) {
  const { id } = payload;

  // Fix for incorrect URL formatting when ID is passed as a result parameter and :id is at the end of the API URL
  const consolidatedInvoiceApi = new Api(`agencies/invoices/consolidated/${id}`);

  try {
    const result = yield call(sendRequest, consolidatedInvoiceApi.load, {}, notifications.REQUEST_ERROR);

    if (result?.data?.success?.data) {
      const { consolidated_invoice } = result.data.success.data;

      yield put(actions.showConsolidatedInvoiceSuccess(consolidated_invoice));
    } else {
      yield put(actions.showConsolidatedInvoiceError());
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.DELETE, deleteJob),
    takeEvery(actions.SAVE, updateJob),
    takeEvery(actions.SEND_REQUEST, sendActionRequest),
    takeEvery(actions.APPROVE_JOB_REQUEST, approveJobRequest),
    takeEvery(actions.PAUSE_MA_REQUEST, onPauseMaRequest),
    takeEvery(actions.RESUME_MA_REQUEST, onResumeMaRequest),
    takeEvery(actions.GET_PAUSE_REASONS, getPauseReasonsRequest),
    takeEvery(actions.GET_ROSTERS, getRosters),
    takeEvery(actions.LOAD_SHIFTS, loadShifts),
    takeEvery(actions.LOAD_CANCELLED_SHIFTS, loadCancelledShifts),
    takeEvery(actions.LOAD_BACKUP_SHIFTS, loadBackupShifts),
    takeEvery(actions.LOAD_REPORTED_SHIFTS, loadReportedShifts),
    takeEvery(actions.REOPEN_POOLS_REQUEST, reopenPools),
    takeEvery(actions.SHOW_CONSOLIDATED_INVOICE_REQUEST, showConsolidatedInvoice),
    takeEvery(actions.LOAD_WORKER_SUBMISSIONS_REQUEST, loadWorkerSubmissions),
    takeEvery(actions.LOAD_APPROVED_AFTER_JOB_CLOSED_REQUEST, loadApprovedAfterJobClosedRequest),
  ]);
}
